import { Tooltip } from '../Tooltip';
import { Icon } from '../../atoms/Icon';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The viewer's side panels, drawn the design's way ("Viewer: mammo annotation"): a panel on the viewer surface, its
 * tabs as icons named in a tooltip, the active one underlined in the light plum, and a wider right panel — the annotation
 * form needs the room, 320px since the form's compact type scale (2026-10-23), which gives the images ~50px. Collapsed, a panel is a narrow strip of icons.
 */
const expandedWidths = { left: 248, right: 320 };
const collapsedWidth = 25;
const borderSize = 1;
const closeIconWidth = 30;

const baseClasses =
  'transition-all duration-300 ease-in-out h-100 bg-dl-surface justify-start box-content flex flex-col';

const classesMap = {
  open: {
    left: 'border-r border-dl-border',
    right: 'border-l border-dl-border',
  },
  closed: {
    left: 'mr-2 items-end border-r border-dl-border',
    right: 'ml-2 items-start border-l border-dl-border',
  },
};

const openStateIconName = {
  left: 'push-left',
  right: 'push-right',
};

function panelStyle(side: 'left' | 'right', open: boolean) {
  const width = expandedWidths[side];
  const hidden = width - collapsedWidth - borderSize;
  return {
    maxWidth: `${width}px`,
    width: `${width}px`,
    // aligned with the viewport grid's top, without overflowing it
    position: 'relative' as const,
    top: '0.2%',
    height: '99.8%',
    ...(side === 'left'
      ? { marginLeft: open ? '0px' : `-${hidden}px` }
      : { marginRight: open ? '0px' : `-${hidden}px` }),
  };
}

/** A tab's icon, in the panel's colours: the active one in full text colour, the others muted. */
function TabIcon({ tab, active }) {
  const className = active ? 'text-dl-text' : 'text-dl-muted';
  const style = { width: '22px', height: '22px' };
  if (tab.iconName) {
    return <Icon name={tab.iconName} className={className} style={style} />;
  }
  if (tab.icon) {
    return React.cloneElement(tab.icon, {
      className: classnames(tab.icon.props.className, className),
      style,
    });
  }
  return null;
}

const SidePanel = ({
  side,
  className,
  activeTabIndex: activeTabIndexProp,
  tabs,
  onOpen,
  onActiveTabIndexChange,
}) => {
  const { t } = useTranslation('SidePanel');

  const [panelOpen, setPanelOpen] = useState(activeTabIndexProp !== null);
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const openStatus = panelOpen ? 'open' : 'closed';

  const updatePanelOpen = useCallback(
    (panelOpen: boolean) => {
      setPanelOpen(panelOpen);
      if (panelOpen && onOpen) {
        onOpen();
      }
    },
    [onOpen]
  );

  const updateActiveTabIndex = useCallback(
    (activeTabIndex: number) => {
      if (activeTabIndex === null) {
        updatePanelOpen(false);
        return;
      }

      setActiveTabIndex(activeTabIndex);
      updatePanelOpen(true);

      if (onActiveTabIndexChange) {
        onActiveTabIndexChange({ activeTabIndex });
      }
    },
    [onActiveTabIndexChange, updatePanelOpen]
  );

  useEffect(() => {
    updateActiveTabIndex(activeTabIndexProp);
  }, [activeTabIndexProp, updateActiveTabIndex]);

  const getCloseStateComponent = () => {
    const _childComponents = Array.isArray(tabs) ? tabs : [tabs];
    return (
      <>
        <button
          type="button"
          aria-label={t('Open the panel')}
          className={classnames(
            'flex h-[46px] w-full cursor-pointer items-center text-dl-muted hover:text-dl-text',
            side === 'left' ? 'justify-end pr-2' : 'justify-start pl-2'
          )}
          onClick={() => {
            updatePanelOpen(!panelOpen);
          }}
          data-cy={`side-panel-header-${side}`}
        >
          <Icon
            name={'navigation-panel-right-reveal'}
            className={classnames(side === 'left' && 'rotate-180 transform')}
          />
        </button>
        <div className={classnames('mt-3 flex flex-col space-y-3')}>
          {_childComponents.map((childComponent, index) => (
            <Tooltip
              position={side === 'left' ? 'right' : 'left'}
              key={index}
              content={childComponent.label}
              className={classnames(
                'flex items-center',
                side === 'left' ? 'justify-end ' : 'justify-start '
              )}
            >
              <div
                id={`${childComponent.name}-btn`}
                data-cy={`${childComponent.name}-btn`}
                className="hover:cursor-pointer"
                onClick={() => {
                  updateActiveTabIndex(index);
                }}
                title={childComponent.iconLabel}
              >
                <TabIcon
                  tab={childComponent}
                  active={index === activeTabIndex}
                />
              </div>
            </Tooltip>
          ))}
        </div>
      </>
    );
  };

  const getCloseIcon = () => {
    return (
      <button
        type="button"
        aria-label={t('Close the panel')}
        className={classnames(
          'flex shrink-0 cursor-pointer items-center justify-center text-dl-muted hover:text-dl-text',
          side === 'left' ? 'order-last' : 'order-first'
        )}
        style={{ width: `${closeIconWidth}px` }}
        onClick={() => {
          updatePanelOpen(!panelOpen);
        }}
        data-cy={`side-panel-header-${side}`}
      >
        <Icon name={openStateIconName[side]} />
      </button>
    );
  };

  /** The tabs as icons, named in a tooltip on hover, the active one underlined in the light plum. */
  const getTabsComponent = () => (
    <div role="tablist" className="flex min-w-0 grow gap-1 overflow-x-auto">
      {tabs.map((tab, tabIndex) => {
        const active = tabIndex === activeTabIndex;
        return (
          <Tooltip
            key={tab.id ?? tabIndex}
            position="bottom"
            content={tab.label}
            className="flex shrink-0"
          >
            <button
              type="button"
              role="tab"
              aria-selected={active}
              aria-label={tab.iconLabel ?? tab.label}
              onClick={() => updateActiveTabIndex(tabIndex)}
              data-cy={`${tab.name}-btn`}
              className={classnames(
                'flex h-[46px] items-center border-b-2 px-2.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dl-accent',
                active
                  ? 'border-dl-accent'
                  : 'border-transparent hover:[&_svg]:text-dl-text'
              )}
            >
              <TabIcon tab={tab} active={active} />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );

  const getOneTabComponent = () => {
    return (
      <button
        type="button"
        className="flex h-[46px] grow cursor-pointer items-center px-2.5 font-sans text-sm font-semibold text-dl-text"
        data-cy={`${tabs[0].name}-btn`}
        onClick={() => updatePanelOpen(!panelOpen)}
      >
        {tabs[0].label}
      </button>
    );
  };

  const getOpenStateComponent = () => {
    return (
      <div className="flex shrink-0 items-stretch border-b border-dl-border px-2">
        {getCloseIcon()}
        {tabs.length === 1 ? getOneTabComponent() : getTabsComponent()}
      </div>
    );
  };

  return (
    <div
      className={classnames(
        className,
        baseClasses,
        classesMap[openStatus][side]
      )}
      style={panelStyle(side, panelOpen)}
    >
      {panelOpen ? (
        <>
          {getOpenStateComponent()}
          {tabs.map((tab, tabIndex) => {
            return React.cloneElement(<tab.content />, {
              key: tab.id,
              className: tabIndex === activeTabIndex ? '' : 'hidden',
            });
          })}
        </>
      ) : (
        <React.Fragment>{getCloseStateComponent()}</React.Fragment>
      )}
    </div>
  );
};

SidePanel.defaultProps = {
  defaultComponentOpen: null,
  activeTabIndex: null, // the default is to close the side panel
};

SidePanel.propTypes = {
  side: PropTypes.oneOf(['left', 'right']).isRequired,
  className: PropTypes.string,
  activeTabIndex: PropTypes.number,
  tabs: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        iconName: PropTypes.string.isRequired,
        iconLabel: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        content: PropTypes.func, // TODO: Should be node, but it keeps complaining?
      })
    ),
  ]),
  onOpen: PropTypes.func,
  onActiveTabIndexChange: PropTypes.func,
};

export default SidePanel;
